// Referral attribution capture with a bounded lifetime.
//
// A ?ref= click stores the referrer's code so a later signup can be credited to
// them. The store used to be write-only and never expire, so a returning
// logged-out visitor who had *ever* clicked a referral link kept seeing the
// "a friend invited you" banner on every clean /login, and the stale code was
// still forwarded into the signup handoff. Every read now goes through
// getActiveReferralCode, which drops a code that is past its capture window —
// or that carries no capture timestamp at all (i.e. was stored before this fix).

import { referralRewardsAvailable } from './native-referral-policy';

export const REFERRAL_CODE_KEY = 'referralCode';
export const REFERRAL_CODE_CAPTURED_AT_KEY = 'referralCodeCapturedAt';

// A referral click is a time-bounded acquisition signal, not a permanent
// property of the browser. 30 days comfortably covers "clicked the link, signs
// up a couple of weeks later" while ensuring a months-old click no longer
// resurrects the banner or forwards a stale code into signup. (Marketing
// attribution in utils/attribution uses a wider 90-day window; a referral
// invite is more time-sensitive, so it gets its own tighter constant.)
export const REFERRAL_CODE_MAX_AGE_DAYS = 30;
const MAX_AGE_MS = REFERRAL_CODE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

// Backend codes are short alphanumerics; reject anything else so a crafted
// ?ref= value can't smuggle URL syntax into the stored code / OAuth handoff.
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

// Storage access must never break the app — these run at module load and inside
// the fetch interceptor. Mirror utils/attribution's safe-wrapper posture.
const safeGet = (storage, key) => {
  try {
    return storage?.getItem(key);
  } catch (_) {
    return null;
  }
};
const safeSet = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch (_) {
    /* storage full / disabled — never break the app over attribution */
  }
};
const safeRemove = (storage, key) => {
  try {
    storage?.removeItem(key);
  } catch (_) {
    /* ignore */
  }
};

/** Remove the referral code and its capture timestamp together. */
export const clearReferralCode = (storage = window.localStorage) => {
  safeRemove(storage, REFERRAL_CODE_KEY);
  safeRemove(storage, REFERRAL_CODE_CAPTURED_AT_KEY);
};

/**
 * Store a valid ?ref= code plus the capture time. No-op for an absent or
 * malformed code, so a clean visit never overwrites or clears a live capture —
 * expiring a stale code is getActiveReferralCode's job, done lazily on read.
 */
export const captureReferralCode = (
  search = window.location.search,
  storage = window.localStorage,
  nowIso = new Date().toISOString()
) => {
  if (!referralRewardsAvailable()) {
    return; // iOS ruling B5: the native app captures no referral code
  }
  const refCode = (new URLSearchParams(search).get('ref') || '').trim();
  if (REFERRAL_CODE_PATTERN.test(refCode)) {
    safeSet(storage, REFERRAL_CODE_KEY, refCode);
    safeSet(storage, REFERRAL_CODE_CAPTURED_AT_KEY, nowIso);
  }
};

/**
 * The single read path for the stored referral code. Returns the code only
 * while it is within the capture window; otherwise clears both keys and returns
 * null. A code with NO capture timestamp predates this fix and is treated as
 * stale — this is what fixes the reported banner-on-a-clean-visit.
 */
export const getActiveReferralCode = (
  storage = window.localStorage,
  nowMs = Date.now()
) => {
  if (!referralRewardsAvailable()) {
    return null; // iOS ruling B5: native sign-ups send no code, so no reward is applied
  }
  const code = safeGet(storage, REFERRAL_CODE_KEY);
  if (!code) {
    return null;
  }
  const capturedAtIso = safeGet(storage, REFERRAL_CODE_CAPTURED_AT_KEY);
  const capturedMs = capturedAtIso ? Date.parse(capturedAtIso) : NaN;
  if (Number.isNaN(capturedMs) || nowMs - capturedMs > MAX_AGE_MS) {
    clearReferralCode(storage);
    return null;
  }
  return code;
};

// The backend pays the referrer only when the signup carries the time the code
// was captured (QLearn-service ReferralService, since #660), and it refuses a
// capture instant later than its own clock by dropping the whole referral,
// including the invited friend's reward. The device clock is not the server's,
// so the instant reported is backdated by this margin: a fast device clock
// cannot turn a fresh code into a "future" one, and an earlier instant only
// makes the server's freshness check stricter.
export const REFERRAL_CAPTURE_CLOCK_MARGIN_MS = 60 * 60 * 1000;
// Reported only for a code captured within the last week. The backend drops
// the whole referral when the instant is older than its freshness TTL, which
// defaults to 30 days but can be overridden in production configuration that
// is not readable from the repository. A week keeps the report far inside any
// plausible TTL while covering the usual click-to-signup delay. An older (but
// still active) code is sent without an instant, exactly as every Google and
// phone signup was before.
export const REFERRAL_CAPTURE_REPORT_MAX_AGE_DAYS = 7;
const REPORTABLE_CAPTURE_MAX_AGE_MS = REFERRAL_CAPTURE_REPORT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

/**
 * The capture instant to send with a signup's referral code, as an ISO-8601
 * string (the backend parses it as a java.time.Instant; a bare number would be
 * read as epoch seconds), or null when there is no active code or the stored
 * instant cannot be reported safely.
 */
export const getReferralCapturedAtForSignup = (
  storage = window.localStorage,
  nowMs = Date.now()
) => {
  if (!getActiveReferralCode(storage, nowMs)) {
    return null;
  }
  const capturedMs = Date.parse(safeGet(storage, REFERRAL_CODE_CAPTURED_AT_KEY));
  const ageMs = nowMs - capturedMs;
  if (Number.isNaN(capturedMs) || ageMs < 0 || ageMs > REPORTABLE_CAPTURE_MAX_AGE_MS) {
    return null;
  }
  return new Date(capturedMs - REFERRAL_CAPTURE_CLOCK_MARGIN_MS).toISOString();
};
